/**
 * Campaign configuration v2 — types that mirror the developer handoff
 * (campaign-ui-api-handoff.html, 2026-09-30). Field names are the backend's;
 * UI words ("outbound content group", "reply instructions") live in the components.
 */

/* ---------- Content references ---------- */

export type TContentKind = 'phone' | 'strategy' | 'conversation' | 'persona'

/** `{id: String(contentId), revisionId: revision.id}` — what a pool stores. */
export interface IContentReference {
   id: string
   revisionId: string
}

/** A catalog row, normalised. The backend's shape varies by kind. */
export interface ICatalogItem {
   contentId: number
   kind: TContentKind
   name: string
   description?: string
   businessTypeId?: number
   /** Shared (read-only template) vs owned by this organization. */
   shared: boolean
   /** Phone content only. */
   callDirections?: Array<'inbound' | 'outbound'>
   callerTypes?: Array<'ai' | 'human'>
   leadTypes?: Array<'known' | 'unknown'>
   /** Which campaign phase the item is written for: ff-lead (new), ff-reactivate (cold), ff-appointment-missed. */
   campaignPhases?: string[]
   /** Strategy content only: series sizes. */
   counts?: { sms: number; email: number; voicemail: number }
   /** Currently published revision id, when the catalog gives it. */
   revisionId?: string
}

/** A stored content revision snapshot (GET /campaign-content/revisions/:id). */
export interface IContentRevision {
   id: string
   contentId: number
   kind: TContentKind
   definition: TContentDefinition
   legacyBusinessTypeId?: string
}

/* ---------- Content definitions (create / customize / revise) ---------- */

export interface ISmsItem {
   id?: number
   template: string
}
export interface IEmailItem {
   id?: number
   subject: string
   body: string
   contentType?: 'text' | 'html'
}
export interface IVoicemailItem {
   id?: number
   template: string
}

export interface IStrategyDefinition {
   name: string
   businessTypeId: number
   sms: ISmsItem[]
   email: IEmailItem[]
   voicemail: IVoicemailItem[]
   data?: Record<string, unknown>
}

export interface IPhoneSection {
   sectionId?: string | number
   name: string
   type:
      | 'call_outbound'
      | 'call_inbound'
      | 'text_outbound'
      | 'text_inbound'
      | 'voicemail'
   order: number
   content: string
   templateType?: string
   protected?: boolean
}

export interface IPhoneDefinition {
   name: string
   businessTypeId: number
   callType: 'outbound' | 'inbound'
   callDirections: Array<'inbound' | 'outbound'>
   leadTypes: Array<'known' | 'unknown'>
   callerTypes?: Array<'ai' | 'human'>
   firstMessage: string
   sections: IPhoneSection[]
}

export interface IConversationDefinition {
   name: string
   businessTypeId: number
   content: string
}

export interface IPersonaDefinition {
   name: string
   businessTypeId: number
   guidance: {
      personaGuidance: string | null
      voiceStyle: string | null
      speakingGuidance: string | null
      conversationStyle: string | null
   }
}

export type TContentDefinition =
   | IStrategyDefinition
   | IPhoneDefinition
   | IConversationDefinition
   | IPersonaDefinition

/* ---------- Campaign configuration (section 7) ---------- */

export type TCriteriaOperator =
   | '='
   | '!='
   | '<>'
   | '>='
   | '<='
   | '>'
   | '<'
   | 'IN'
   | 'NOT IN'
   | 'BETWEEN'
   | 'CONTAINS'
   | 'CONTAINS_ANY'
   | 'CONTAINS_ALL'
   | 'NOT_CONTAINS_ANY'
   | 'LIKE'
   | 'ILIKE'
   | 'IS NULL'
   | 'IS NOT NULL'

export interface ICriteria {
   field: string
   operator: TCriteriaOperator
   value?: Array<string | number> | string | number | boolean
   logic?: 'AND' | 'OR'
}

export type TSource =
   { mode: 'manual' } | { mode: 'dynamic'; criteria: ICriteria[] }

export interface IRouting {
   enabled: boolean
   strategy: 'round_robin' | 'jump_ball'
   agentIds: number[]
}

export interface IInteractionSettings {
   appointmentConfig?: {
      virtualAppointment?: string | null
      virtualAppointmentLength?: string | null
      inPersonAppointment?: string | null
      inPersonAppointmentLength?: string | null
   }
   outboundCall?: Record<string, unknown>
   inboundCall?: Record<string, unknown>
}

export interface IContentPool {
   id: string
   businessTypeId: number
   isDefault: boolean
   outboundScripts: IContentReference[]
   inboundScripts: IContentReference[]
   unknownInboundScripts?: IContentReference[]
   humanScripts?: IContentReference[]
   strategies: IContentReference[]
   conversationContent: IContentReference[]
   personas?: IContentReference[]
   personaReplacements?: Record<string, string>
   interactionSettings?: IInteractionSettings
}

export type TCadenceAction = 'call' | 'sms' | 'email'

export interface ICadenceStep {
   id: string
   order: number
   /** Day 1 = the enrollment calendar day. */
   day: number
   /** 24-hour HH:mm in the campaign timezone. */
   localTime: string
   action: TCadenceAction
   /** Calls only. */
   voicemail?: 'if-configured' | 'none'
}

export interface ISubtype {
   id: string
   name: string
   description: string
   poolId: string
   priority: number
   conditions: ICriteria[]
}

export interface IBusinessTypeRule {
   businessTypeId: number
   conditions: ICriteria[]
}

export interface IStopRules {
   tags?: string[]
   stages?: string[]
}

export interface ICampaignConfiguration {
   schemaVersion: 2
   conversationQuietHours?: number
   businessTypeIds: number[]
   businessTypeRules?: IBusinessTypeRule[]
   source: TSource
   routing: IRouting
   phoneNumbers?: string[]
   contentPools: IContentPool[]
   subtypes: ISubtype[]
   cadence: ICadenceStep[]
   schedulePolicy?: Record<string, unknown>
   stopRules?: IStopRules
   incomingCallPoolId?: string
   selectionPolicy: {
      phone: 'round_robin'
      strategy: 'ordered_journey_v1'
      exhaustion: 'repeat-last'
   }
}

/* ---------- Campaign lifecycle reads ---------- */

export type TCampaignStatus =
   'draft' | 'active' | 'paused' | 'inactive' | string

/** GET /active-lead-sources/:id/configuration */
export interface IConfigurationRead {
   campaignId: string
   name: string
   configuration: ICampaignConfiguration
   revision: number
   status: TCampaignStatus
   publishedVersionId: string | null
   activeVersionId: string | null
   hasUnpublishedChanges: boolean
}

export interface IAssistant {
   id: number | string
   assistantName: string
   voiceId: string
   description?: string
   avatar?: string | null
   previewUrl?: string | null
   gender?: string | null
   /* The rest of the voice's profile, stored on the assistant as the earlier screen did. */
   age?: string | null
   language?: string | null
   accent?: string | null
   designation?: string | null
}

/** GET /active-lead-sources/:id (the parts this editor reads). */
export interface ILiveCampaign {
   id: string
   campaignName: string
   status: TCampaignStatus
   aiAssistants: IAssistant[]
   phoneNumbers: string[]
   agentAssignmentConfig: IRouting | null
   /** ISO date of the last change, when the backend gives one. */
   lastUpdated?: string
   /** The backend's own campaign type name, when it gives one. */
   campaignType?: string
}

/** GET /active-lead-sources/:id/configuration/preview */
export interface IPreview {
   ready: boolean
   reasons: string[]
   cadence: ICadenceStep[]
   selectedPhoneNumbers: string[]
   existingPhoneNumberCount: number
   numbersToPurchase: number
   /** The numbers the backend would buy on launch, when it lists them. */
   purchaseNumbers?: string[]
   contentManifest?: unknown
}

export interface IReadiness {
   enabled: boolean
   ready: boolean
   reasons: string[]
}

export interface IBusinessCategory {
   id: number
   name: string
   slug?: string
}

export interface IVoice {
   voiceId: string
   name: string
   description?: string
   previewUrl?: string | null
   avatar?: string | null
   gender?: string | null
   accent?: string | null
   language?: string | null
   age?: string | null
}

export interface IAgent {
   crmUserId: number
   name: string
   email?: string
   phone?: string | null
}

export interface IInventoryNumber {
   isOrganizationNumber?: boolean
   phoneNumber: string
   /** "purchased", or "reserved" for a draft and bought only when that draft launches. */
   status?: string
   locality?: string
   region?: string
   isUsedInCampaigns?: boolean
   campaignIds?: string[]
}

export interface IAvailableNumber {
   phoneNumber: string
   locality?: string
   region?: string
   friendlyName?: string
   capabilities?: { voice: boolean; SMS: boolean; MMS: boolean }
   [key: string]: unknown
}

export interface INumberPricing {
   currentPeriod?: { amount: number; currency?: string }
   nextPeriod?: { amount: number; currency?: string }
   [key: string]: unknown
}

export interface IOrgAnswering {
   receiver: 'ai' | 'forward'
   forwardNumber?: string | null
   forwardAgentIds?: number[]
}

/** Error the adapter raises so the UI can branch on the backend's code. */
export class CampaignV2Error extends Error {
   status: number
   code?: string
   constructor(message: string, status: number, code?: string) {
      super(message)
      this.name = 'CampaignV2Error'
      this.status = status
      this.code = code
   }
}
